/**
 * Speed-over-time chart with a moving playhead reference line.
 *
 * Reuses host's recharts (no extra dep). Throttles re-renders by accepting
 * the playhead value via prop — parent already rate-limits via rAF.
 *
 * Click anywhere on the chart → `onSeek(utcMs)` to the nearest GPS
 * sample. Same idiom as ImuChart + Map; H4 ensures all three panes
 * snap to a real sample's UTC so seek semantics are uniform across
 * the synced viewer.
 */

import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { GpsSummary } from '../../types';
import { nearestSampleByUtc } from './util';

interface SpeedChartProps {
  summary: GpsSummary;
  currentUtcMs: number | null;
  unit?: 'mps' | 'kmh';
  onSeek?: (utcMs: number) => void;
  /** Clamp the chart's left edge to this UTC ms — the video's first
   *  PROGRAM-DATE-TIME. Hides the pre-video sensor pre-roll so the
   *  playhead cursor aligns with the seek bar at t=0. Null → dataMin.
   *  (See ImuChart's `domainStartMs` for the full rationale.) */
  domainStartMs?: number | null;
  /** Edge-to-edge variant for the public viewer's narrow panels: hides the
   *  left km/h axis (scale kept, width reclaimed) and drops the phantom
   *  right axis + tightens the right margin so the plot spans full width.
   *  Default false keeps the operator synced-viewer layout (axis labels +
   *  the 60px right reservation that aligns it with ImuChart). */
  compact?: boolean;
  /** Subtle extra reference marks (no labels, not connected): the furthest
   *  buffered/playable UTC (dashed accent) and the live/telemetry edge
   *  (dotted neutral). Order on the axis: playhead ≤ loaded ≤ live. */
  loadedEdgeUtc?: number | null;
  liveEdgeUtc?: number | null;
}

export const SpeedChart: React.FC<SpeedChartProps> = ({
  summary, currentUtcMs, unit = 'kmh', onSeek, domainStartMs, compact = false,
  loadedEdgeUtc = null, liveEdgeUtc = null,
}) => {
  const factor = unit === 'kmh' ? 3.6 : 1;
  const data = summary.track.map(([, , utc, mps]) => ({
    t: utc,
    v: mps * factor,
  }));

  const handleClick = (state: unknown) => {
    if (!onSeek) return;
    const s = state as { activeLabel?: number | string } | null;
    if (!s) return;
    const t = Number(s.activeLabel);
    if (!Number.isFinite(t)) return;
    const nearest = nearestSampleByUtc(data, t);
    onSeek(nearest ? nearest.t : t);
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        data={data}
        margin={{ top: 8, right: compact ? 4 : 12, bottom: 8, left: 0 }}
        onClick={handleClick}
      >
        <CartesianGrid stroke="#222" strokeDasharray="3 3" />
        <XAxis
          dataKey="t"
          type="number"
          scale="time"
          domain={[domainStartMs ?? 'dataMin', 'dataMax']}
          allowDataOverflow
          tickFormatter={(t) => new Date(t).toISOString().substr(11, 8)}
          stroke="#666"
          tick={{ fontSize: 10 }}
          tickCount={5}
        />
        {compact ? (
          // Edge-to-edge: keep the scale (Area references yAxisId="speed")
          // but hide the axis so its width is reclaimed for the plot.
          <YAxis yAxisId="speed" hide />
        ) : (
          <YAxis
            yAxisId="speed"
            stroke="#666"
            tick={{ fontSize: 10 }}
            label={{ value: unit === 'kmh' ? 'km/h' : 'm/s', position: 'insideTopLeft', fill: '#666', fontSize: 10 }}
          />
        )}
        {/* Phantom right-side YAxis — reserves the same ~60 px on the
            right that ImuChart's `yAxisId="jerk"` axis occupies, so the
            two charts' data areas line up vertically. Without this the
            playhead reference line at the same UTC sat at different
            X-pixel positions in the two charts. No data references this
            axis; recharts leaves it empty + we hide the visuals.
            Skipped in compact (public viewer, no paired ImuChart). */}
        {!compact && (
          <YAxis
            yAxisId="phantom-right"
            orientation="right"
            tick={false}
            axisLine={false}
            tickLine={false}
            width={60}
          />
        )}
        <Tooltip
          contentStyle={{ backgroundColor: '#14141f', border: '1px solid #2a2a35', color: '#ccc', fontSize: 12 }}
          labelFormatter={(t) => new Date(Number(t)).toISOString().replace('T', ' ').replace('Z', '')}
          formatter={(value: any) => [`${Number(value).toFixed(1)} ${unit === 'kmh' ? 'km/h' : 'm/s'}`, 'speed']}
        />
        <Area
          yAxisId="speed"
          type="monotone"
          dataKey="v"
          stroke="#00ff00"
          fill="#00ff00"
          fillOpacity={0.18}
          isAnimationActive={false}
        />
        {/* Video-loaded edge — furthest playable UTC (dashed, dim accent). */}
        {loadedEdgeUtc != null && (
          <ReferenceLine
            yAxisId="speed"
            x={loadedEdgeUtc}
            stroke="#00c8ff"
            strokeOpacity={0.4}
            strokeWidth={1}
            strokeDasharray="4 3"
          />
        )}
        {/* Live / telemetry edge — newest sample (dotted, neutral, faint). */}
        {liveEdgeUtc != null && (
          <ReferenceLine
            yAxisId="speed"
            x={liveEdgeUtc}
            stroke="#888888"
            strokeOpacity={0.35}
            strokeWidth={1}
            strokeDasharray="1 3"
          />
        )}
        {/* Playhead — solid, on top. */}
        {currentUtcMs != null && (
          <ReferenceLine yAxisId="speed" x={currentUtcMs} stroke="#00c8ff" strokeWidth={2} />
        )}
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default SpeedChart;
